// frontend/src/widgets/AdminMaintenanceWidget.jsx
// Project:   Vision ANPR - Admin/Maintenance Widget
// Purpose:   Admin-only destructive maintenance controls for jobs, quotes, opportunities,
//            work packages, tasks, expenses, contacts, users, resources, customers,
//            locations, and database backup/restore operations.
// Lifecycle:  Active dashboard widget. Desktop behaviour is canonical; mobile layout
//            must reflow without duplicating mounts, state, effects, or API requests.
// Updated:   2026-10-30 11:30 GMT
//
// Change summary:
//   • 2026-10-30 11:30 GMT: Wire guarded admin hard-delete for Quotes and Opportunities;
//     display structured blocking IDs from HTTP 409; preserve other controls.
//   • 2026-10-30 11:12 GMT: Corrected Quote deletion to verified /api/quotes/:id,
//     requiring a successful dependency preflight and displaying blocking record IDs.
//     Opportunity offers dependency inspection only: hard-delete route does not exist.
//     No backend routes, other maintenance controls or API contracts changed.
//   • 2026-10-30 10:38 GMT: Restored Quote and Opportunity UI (routes were unverified).
//   • 2026-10-26: Added administrator-only Expense deletion by ID in Data Integrity.
//     Uses existing tenant-scoped DELETE /api/expenses/:id; confirmation, toast and refresh.
//   • 2026-10-20: A3.3 guarded Work Package deletion in existing Data Integrity panel.
//   • 2026-10-14: Added Data Integrity deep-delete control for individual runtime tasks.
//     Uses /admin/tasks/:id and deliberately does not modify workflow template definitions.
//   • Mobile responsiveness pass: preserve all existing maintenance flows and desktop UX.
//   • Stack action controls below 768px and promote buttons/inputs to touch-friendly targets.
//   • Constrain toasts, backup filenames, and native file input to narrow mobile viewports.
//   • Preserve the existing single backup-list fetch and all current API/event behaviour.
//
// Previous updates:
//   • 2026-01-06: Delete User multi-endpoint fallback
//     (/admin/users/:id → /admin/user/:id → /users/:id).
//   • Rich error reporting (status, text/json), prevent self-delete, Enter-to-delete QoL.
//   • Preserved theme-aware buttons and all existing maintenance flows.
//   • Redact Resource: admin endpoint + optional reassignment.
//   • Toast severity mapping (info on success, critical on failure) + readable styling
//     with no Tailwind dependency for severity colours.

import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const Toast = ({ message, type = "success", onClose }) => {
  if (!message) return null;

  // Do NOT depend on Tailwind utility classes here (purge can nuke them).
  // Use hard inline colours so this remains readable in every build.
  const stylesByType = {
    success: { backgroundColor: "#059669" }, // emerald-600
    info: { backgroundColor: "#2563eb" }, // blue-600
    error: { backgroundColor: "#dc2626" }, // red-600
    critical: { backgroundColor: "#b91c1c" }, // red-700
    neutral: { backgroundColor: "#334155" }, // slate-700
  };

  const style = stylesByType[type] || stylesByType.neutral;

  return (
    <div className="fixed inset-x-3 bottom-3 z-[9999] md:inset-x-auto md:bottom-5 md:right-5">
      <div
        className="w-full max-w-full md:w-auto md:max-w-md text-white shadow-lg rounded-lg px-4 py-3 text-sm flex items-start gap-3"
        style={style}
      >
        <span className="min-w-0 flex-1 break-words">{message}</span>
        <button
          onClick={onClose}
          className="ml-2 -mr-1 -my-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-white/80 hover:text-white focus:outline-none flex items-center justify-center"
          aria-label="Close toast"
          title="Close"
        >
          ×
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
    <div className="px-3 py-3 md:px-4 text-sm font-semibold border-b">{title}</div>
    <div className="p-3 md:p-4 space-y-4">{children}</div>
  </div>
);

// Reusable themed button variants
const BtnAction = ({ disabled, children, ...props }) => (
  <button
    {...props}
    disabled={disabled}
    className={`w-full md:w-auto min-h-[44px] md:min-h-0 bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick disabled:opacity-60 text-white text-sm font-semibold py-2 px-3 rounded shadow ${props.className || ""}`}
  >
    {children}
  </button>
);

const BtnGeneral = ({ disabled, children, ...props }) => (
  <button
    {...props}
    disabled={disabled}
    className={`w-full md:w-auto min-h-[44px] md:min-h-0 bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:opacity-60 text-white text-sm font-semibold py-2 px-3 rounded shadow ${props.className || ""}`}
  >
    {children}
  </button>
);

// If you have danger tokens in your theme, these class names will pick them up.
// Fallback to Action palette if your CSS doesn't define them (keeps things themed).
const BtnDanger = ({ disabled, children, ...props }) => (
  <button
    {...props}
    disabled={disabled}
    className={`w-full md:w-auto min-h-[44px] md:min-h-0 bg-btnDangerNormal hover:bg-btnDangerHover active:bg-btnDangerClick disabled:opacity-60 text-white text-sm font-semibold py-2 px-3 rounded shadow ${props.className || ""}`}
    style={{
      // Fallbacks if btnDanger* custom classes aren’t present:
      backgroundColor: "var(--btn-danger-normal, var(--btn-action-normal))",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--btn-danger-hover, var(--btn-action-hover))")}
    onMouseDown={(e) => (e.currentTarget.style.backgroundColor = "var(--btn-danger-click, var(--btn-action-click))")}
    onMouseUp={(e) => (e.currentTarget.style.backgroundColor = "var(--btn-danger-hover, var(--btn-action-hover))")}
    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--btn-danger-normal, var(--btn-action-normal))")}
  >
    {children}
  </button>
);

// ---- helpers ----
const extractAxiosError = (err) => {
  try {
    const status = err?.response?.status;
    const data = err?.response?.data;
    let body = "";
    if (typeof data === "string") body = data;
    else if (data && typeof data === "object") body = JSON.stringify(data);
    else body = err?.message || "Unknown error";
    return { status, body };
  } catch {
    return { status: undefined, body: err?.message || "Unknown error" };
  }
};

const toOptionalNumber = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

const AdminMaintenanceWidget = () => {
  const [toast, setToast] = useState({ message: "", type: "success" });
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => setToast({ message: "", type: "success" }), 3500);
  };

  // Delete Job
  const [jobId, setJobId] = useState("");
  const [deletingJob, setDeletingJob] = useState(false);
  const deleteJob = async () => {
    const id = Number(jobId);
    if (!id || Number.isNaN(id)) return;
    if (!window.confirm(`This will permanently delete job #${id} and all related records. Continue?`)) return;
    setDeletingJob(true);
    try {
      await axiosInstance.delete(`/admin/jobs/${id}`);
      showToast(`Job #${id} deleted`, "success");
      setJobId("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Job delete failed${status ? ` (${status})` : ""}: ${body}`, "critical");
      console.error("Admin delete job failed:", e);
    } finally {
      setDeletingJob(false);
    }
  };

  // Data Integrity: existing read-only dependency API responses are informative only.
  // The backend remains the final deletion authority; never bypass its constraints.
  const [quoteId, setQuoteId] = useState("");
  const [deletingQuote, setDeletingQuote] = useState(false);
  const [quoteInspection, setQuoteInspection] = useState(null);
  const [opportunityId, setOpportunityId] = useState("");
  const [inspectingOpportunity, setInspectingOpportunity] = useState(false);
  const [opportunityInspection, setOpportunityInspection] = useState(null);

  const describeInspectionError = (kind, id, e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    const message = typeof detail === "string" ? detail : (detail?.message || e?.message || "Request failed");
    if (status === 404) return `${kind} #${id} was not found or is not visible to this account.`;
    if (status === 403) return `Access denied for ${kind} #${id}: ${message}`;
    if (status === 409) return `${kind} #${id} is blocked: ${message}`;
    return `Unable to inspect ${kind} #${id}${status ? ` (${status})` : ""}: ${message}`;
  };

  const renderDeleteError = (kind, id, error, setInspection) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 409 && detail && typeof detail === "object") {
      setInspection({ id, data: {
        dependency_groups: detail.dependency_groups || [],
        recommendation: detail.message || "Deletion blocked by database integrity protection.",
      }});
      showToast(detail.message || `Cannot delete ${kind} #${id}: dependency conflict`, "critical");
      return;
    }
    showToast(describeInspectionError(kind, id, error), "critical");
  };

  const deleteQuote = async () => {
    const id = Number(quoteId);
    if (!Number.isSafeInteger(id) || id < 1 || deletingQuote) return;
    setDeletingQuote(true);
    setQuoteInspection(null);
    try {
      if (!window.confirm(`Permanently delete Quote #${id} and its quote-owned records? Linked jobs and protected records will block deletion. This cannot be undone.`)) return;
      await axiosInstance.delete(`/admin/quotes/${id}`);
      showToast(`Quote #${id} deleted`, "success");
      setQuoteId("");
      setQuoteInspection(null);
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      renderDeleteError("Quote", id, e, setQuoteInspection);
      console.error("Admin Quote deletion failed:", e);
    } finally {
      setDeletingQuote(false);
    }
  };

  const deleteOpportunity = async () => {
    const id = Number(opportunityId);
    if (!Number.isSafeInteger(id) || id < 1 || inspectingOpportunity) return;
    setInspectingOpportunity(true);
    setOpportunityInspection(null);
    try {
      if (!window.confirm(`Permanently delete Opportunity #${id} and its opportunity-owned records? Linked quotes and jobs will block deletion. This cannot be undone.`)) return;
      await axiosInstance.delete(`/admin/opportunities/${id}`);
      showToast(`Opportunity #${id} deleted`, "success");
      setOpportunityId("");
      setOpportunityInspection(null);
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      renderDeleteError("Opportunity", id, e, setOpportunityInspection);
      console.error("Admin Opportunity deletion failed:", e);
    } finally {
      setInspectingOpportunity(false);
    }
  };

  const DependencyDetails = ({ inspection, kind }) => {
    if (!inspection) return null;
    const groups = (inspection.data?.dependency_groups || []).filter((g) => Number(g.count) > 0);
    const blockers = groups.filter((g) => g.blocking_delete);
    return (
      <div className="md:col-span-3 min-w-0 rounded-lg border border-gray-300 bg-gray-50 p-3 space-y-2 text-sm" role="status">
        <div className="font-semibold">
          {blockers.length
            ? `Cannot delete ${kind} #${inspection.id} — ${blockers.length} blocking dependency group(s)`
            : `${kind} #${inspection.id} — no blocking records in preview`}
        </div>
        {groups.map((group) => (
          <div key={group.key} className="border-t border-gray-200 pt-2">
            <div className="font-medium">{group.label}: {group.count} {group.blocking_delete ? "(BLOCKING)" : "(informational)"}</div>
            {(group.records || []).map((record, index) => (
              <div key={`${group.key}-${record.id ?? index}`} className="break-words text-gray-700">
                {group.label} #{record.id ?? "?"}{record.label ? ` — ${record.label}` : ""}{record.status ? ` (${record.status})` : ""}
              </div>
            ))}
            {group.note && <div className="text-xs text-gray-600">{group.note}</div>}
            {Number(group.count) > (group.records || []).length && (
              <div className="text-xs text-gray-600">Additional linked records are not shown in this preview.</div>
            )}
          </div>
        ))}
        {!groups.length && <div className="text-gray-600">No linked records reported.</div>}
        {inspection.data?.recommendation && <div className="text-xs text-gray-700">{inspection.data.recommendation}</div>}
        <div className="text-xs text-gray-600">The backend determines whether deletion is permitted and reports protected dependencies.</div>
      </div>
    );
  };

  // Delete Work Package: backend refuses deletion if work has been recorded.
  const [workPackageId, setWorkPackageId] = useState("");
  const [deletingWorkPackage, setDeletingWorkPackage] = useState(false);
  const deleteWorkPackage = async () => {
    const id = Number(workPackageId);
    if (!Number.isSafeInteger(id) || id < 1) return;
    if (!window.confirm(`Permanently delete Work Package #${id} and its unstarted runtime records? The parent Job and shared Locations remain. Deletion is blocked if work has been recorded.`)) return;
    setDeletingWorkPackage(true);
    try {
      await axiosInstance.delete(`/admin/work-packages/${id}`);
      showToast(`Work Package #${id} deleted`, "success");
      setWorkPackageId("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Work Package deletion blocked/failed${status ? ` (${status})` : ""}: ${body}`, "critical");
    } finally {
      setDeletingWorkPackage(false);
    }
  };

  // Delete Runtime Task
  const [taskId, setTaskId] = useState("");
  const [deletingTask, setDeletingTask] = useState(false);
  const deleteTask = async () => {
    const id = Number(taskId);
    if (!id || Number.isNaN(id)) return;
    if (
      !window.confirm(
        `This will permanently delete runtime task #${id} and its task-owned runtime records. The workflow template definition will not be changed. Continue?`
      )
    )
      return;

    setDeletingTask(true);
    try {
      await axiosInstance.delete(`/admin/tasks/${id}`);
      showToast(`Runtime task #${id} deleted`, "success");
      setTaskId("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Task delete failed${status ? ` (${status})` : ""}: ${body}`, "critical");
      console.error("Admin delete runtime task failed:", e);
    } finally {
      setDeletingTask(false);
    }
  };

  // Delete Expense: API enforces Super Admin (1) / System Admin (3) and tenant isolation.
  const [expenseId, setExpenseId] = useState("");
  const [deletingExpense, setDeletingExpense] = useState(false);
  const deleteExpense = async () => {
    const id = Number(expenseId);
    if (!Number.isSafeInteger(id) || id < 1) return;
    if (!window.confirm(`Permanently delete expense #${id}? This action cannot be undone. Continue?`)) return;
    setDeletingExpense(true);
    try {
      await axiosInstance.delete(`/api/expenses/${id}`);
      showToast(`Expense #${id} deleted`, "success");
      setExpenseId("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Expense delete failed${status ? ` (${status})` : ""}: ${body}`, "critical");
      console.error("Admin delete expense failed:", e);
    } finally {
      setDeletingExpense(false);
    }
  };

  // Delete Contact
  const [contactId, setContactId] = useState("");
  const [detachJobs, setDetachJobs] = useState(true);
  const [deletingContact, setDeletingContact] = useState(false);
  const deleteContact = async () => {
    const id = Number(contactId);
    if (!id || Number.isNaN(id)) return;
    if (!window.confirm(`Delete contact #${id}? ${detachJobs ? "Jobs will be detached (contact_id=NULL)." : "Jobs for this contact will be deleted."}`))
      return;
    setDeletingContact(true);
    try {
      await axiosInstance.delete(`/admin/contacts/${id}`, { params: { detach_jobs: detachJobs } });
      showToast(`Contact #${id} deleted`, "success");
      setContactId("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Contact delete failed${status ? ` (${status})` : ""}: ${body}`, "critical");
      console.error("Admin delete contact failed:", e);
    } finally {
      setDeletingContact(false);
    }
  };

  // Delete User (hardened with fallbacks)
  const [userId, setUserId] = useState("");
  const [deletingUser, setDeletingUser] = useState(false);
  const deleteUser = async () => {
    const id = Number(userId);
    if (!id || Number.isNaN(id)) return;

    try {
      const me = JSON.parse(localStorage.getItem("anpr_user") || "null");
      if (me?.id && Number(me.id) === id) {
        showToast("You cannot delete the currently logged-in user.", "critical");
        return;
      }
    } catch {
      /* ignore */
    }

    if (!window.confirm(`Delete user #${id}? This action cannot be undone.`)) return;

    setDeletingUser(true);
    const candidates = [`/admin/users/${id}`, `/admin/user/${id}`, `/users/${id}`];

    try {
      let lastErr = null;
      for (const url of candidates) {
        try {
          await axiosInstance.delete(url);
          showToast(`User #${id} deleted`, "success");
          setUserId("");
          window.dispatchEvent(new CustomEvent("dashboard:refresh"));
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (lastErr) {
        const { status, body } = extractAxiosError(lastErr);
        showToast(`User delete failed${status ? ` (${status})` : ""}: ${body}`, "critical");
        console.error("Admin delete user failed (all routes tried):", lastErr);
      }
    } finally {
      setDeletingUser(false);
    }
  };

  // Redact Resource
  const [resourceId, setResourceId] = useState("");
  const [replacementResourceId, setReplacementResourceId] = useState("");
  const [redactingResource, setRedactingResource] = useState(false);

  const redactResource = async () => {
    const rid = Number(resourceId);
    if (!rid || Number.isNaN(rid)) return;

    const rep = toOptionalNumber(replacementResourceId);
    if (rep && rep === rid) {
      showToast("Replacement Resource ID cannot be the same as the Resource ID.", "critical");
      return;
    }

    const extra = rep
      ? `References will be reassigned to resource #${rep} first, then PII will be redacted.`
      : "PII will be redacted and the resource will be deactivated. No reassignment will be performed.";

    if (!window.confirm(`Redact resource #${rid}? ${extra} Continue?`)) return;

    setRedactingResource(true);

    const candidates = [
      { method: "post", url: `/admin/resources/${rid}/redact` },
      { method: "post", url: `/admin/resource/${rid}/redact` },
      { method: "post", url: `/admin/resources/redact/${rid}` },
      { method: "post", url: `/resources/${rid}/redact` },
    ];

    const payload = rep ? { replacement_resource_id: rep } : {};
    const params = rep ? { replacement_resource_id: rep } : undefined;

    try {
      let lastErr = null;
      let resData = null;

      for (const c of candidates) {
        try {
          const res = await axiosInstance.post(c.url, payload, params ? { params } : undefined);
          resData = res?.data ?? null;
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      if (lastErr) {
        const { status, body } = extractAxiosError(lastErr);
        showToast(`Resource redaction failed${status ? ` (${status})` : ""}: ${body}`, "critical");
        console.error("Admin redact resource failed (all routes tried):", lastErr);
        return;
      }

      // Success: use INFO (as requested) and include reassignment summary if present
      const reassignedCount = (() => {
        const reassigned = resData?.reassigned;
        if (!reassigned || typeof reassigned !== "object") return 0;
        return Object.values(reassigned).reduce((acc, n) => acc + (Number(n) || 0), 0);
      })();

      const msg = rep
        ? `Resource #${rid} redacted. Reassigned ${reassignedCount} linked records to resource #${rep}.`
        : `Resource #${rid} redacted.`;

      showToast(msg, "info");

      setResourceId("");
      setReplacementResourceId("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } finally {
      setRedactingResource(false);
    }
  };

  // Delete Customer
  const [customerId, setCustomerId] = useState("");
  const [cascadeJobs, setCascadeJobs] = useState(false);
  const [cascadeContacts, setCascadeContacts] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const deleteCustomer = async () => {
    const id = Number(customerId);
    if (!id || Number.isNaN(id)) return;
    const warn = [cascadeJobs ? "ALL jobs" : "No jobs", cascadeContacts ? "contacts" : "no contacts"].join(" & ");
    if (!window.confirm(`Delete customer #${id}? This will affect: ${warn}. Continue?`)) return;
    setDeletingCustomer(true);
    try {
      await axiosInstance.delete(`/admin/customers/${id}`, {
        params: { cascade_jobs: cascadeJobs, cascade_contacts: cascadeContacts },
      });
      showToast(`Customer #${id} deleted`, "success");
      setCustomerId("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Customer delete failed${status ? ` (${status})` : ""}: ${body}`, "critical");
      console.error("Admin delete customer failed:", e);
    } finally {
      setDeletingCustomer(false);
    }
  };

  // Delete Location
  const [locationId, setLocationId] = useState("");
  const [reassignTo, setReassignTo] = useState("");
  const [deletingLocation, setDeletingLocation] = useState(false);
  const deleteLocation = async () => {
    const id = Number(locationId);
    if (!id || Number.isNaN(id)) return;
    const to = reassignTo ? Number(reassignTo) : undefined;
    const suffix = to ? `Jobs will be reassigned to location #${to}.` : "Jobs will have location cleared.";
    if (!window.confirm(`Delete location #${id}? ${suffix} Continue?`)) return;
    setDeletingLocation(true);
    try {
      await axiosInstance.delete(`/admin/locations/${id}`, { params: { reassign_to: to } });
      showToast(`Location #${id} deleted`, "success");
      setLocationId("");
      setReassignTo("");
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Location delete failed${status ? ` (${status})` : ""}: ${body}`, "critical");
      console.error("Admin delete location failed:", e);
    } finally {
      setDeletingLocation(false);
    }
  };

  // Backup / Restore
  const [backingUp, setBackingUp] = useState(false);
  const [backups, setBackups] = useState([]);
  const doBackup = async () => {
    if (!window.confirm("Create database backup now?")) return;
    setBackingUp(true);
    try {
      const res = await axiosInstance.post("/admin/backup");
      showToast(`Backup created: ${res?.data?.filename || "OK"}`, "success");
      listBackups();
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Backup failed${status ? ` (${status})` : ""}: ${body}`, "critical");
    } finally {
      setBackingUp(false);
    }
  };
  const listBackups = async () => {
    try {
      const res = await axiosInstance.get("/admin/backups");
      setBackups(res?.data?.backups || []);
    } catch {
      setBackups([]);
    }
  };
  useEffect(() => {
    listBackups();
  }, []);

  const [restoreFile, setRestoreFile] = useState(null);
  const [truncateBefore, setTruncateBefore] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const doRestore = async () => {
    if (!restoreFile) {
      showToast("Pick a .sql dump to restore", "critical");
      return;
    }
    const msg = truncateBefore
      ? "⚠️ This will DROP and recreate schema before restore. Are you absolutely sure?"
      : "Apply SQL to current database. Continue?";
    if (!window.confirm(msg)) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("truncate_before", String(!!truncateBefore));
      await axiosInstance.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      showToast("Restore completed", "success");
      setRestoreFile(null);
      const el = document.getElementById("restore-file-input");
      if (el) el.value = "";
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      const { status, body } = extractAxiosError(e);
      showToast(`Restore failed${status ? ` (${status})` : ""}: ${body}`, "critical");
    } finally {
      setRestoring(false);
    }
  };

  const onEnter = (e, fn) => {
    if (e.key === "Enter") fn();
  };

  return (
    <div className="h-full w-full min-w-0 overflow-auto rounded-lg border border-gray-200 shadow-sm bg-white p-3 md:p-4 space-y-4 md:space-y-6">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="text-lg font-semibold">Admin / Maintenance</div>
      <div className="text-xs text-gray-600 -mt-2 mb-2">
        Admin-only destructive tools. The backend enforces privileges; this UI just calls it.
      </div>

      <Section title="Delete Job (deep)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Job ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteJob)}
              placeholder="e.g. 42"
            />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={deleteJob} disabled={!jobId || deletingJob}>
              {deletingJob ? "Deleting…" : "Delete Job (deep)"}
            </BtnDanger>
            <BtnGeneral onClick={() => setJobId("")} disabled={!jobId}>
              Clear
            </BtnGeneral>
          </div>
        </div>
      </Section>

      <Section title="Delete Quote (dependency-guarded)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Quote ID</label>
            <input className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={quoteId} onChange={(e) => { setQuoteId(e.target.value); setQuoteInspection(null); }}
              onKeyDown={(e) => onEnter(e, deleteQuote)} placeholder="e.g. 42" inputMode="numeric" />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={deleteQuote} disabled={!Number.isSafeInteger(Number(quoteId)) || Number(quoteId) < 1 || deletingQuote}>
              {deletingQuote ? "Deleting…" : "Delete Quote"}
            </BtnDanger>
            <BtnGeneral onClick={() => { setQuoteId(""); setQuoteInspection(null); }} disabled={!quoteId || deletingQuote}>Clear</BtnGeneral>
          </div>
          <DependencyDetails inspection={quoteInspection} kind="Quote" />
        </div>
      </Section>

      <Section title="Delete Opportunity (dependency-guarded)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Opportunity ID</label>
            <input className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={opportunityId} onChange={(e) => { setOpportunityId(e.target.value); setOpportunityInspection(null); }}
              onKeyDown={(e) => onEnter(e, deleteOpportunity)} placeholder="e.g. 42" inputMode="numeric" />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={deleteOpportunity} disabled={!Number.isSafeInteger(Number(opportunityId)) || Number(opportunityId) < 1 || inspectingOpportunity}>
              {inspectingOpportunity ? "Deleting…" : "Delete Opportunity"}
            </BtnDanger>
            <BtnGeneral onClick={() => { setOpportunityId(""); setOpportunityInspection(null); }} disabled={!opportunityId || inspectingOpportunity}>Clear</BtnGeneral>
          </div>
          <div className="md:col-span-3 text-xs text-gray-700">
            Permanently deletes an opportunity and its owned child records. Linked Quotes and Jobs block deletion, with exact record IDs shown below.
          </div>
          <DependencyDetails inspection={opportunityInspection} kind="Opportunity" />
        </div>
      </Section>

      <Section title="Delete Work Package (guarded)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Work Package ID</label>
            <input className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={workPackageId} onChange={(e) => setWorkPackageId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteWorkPackage)} placeholder="e.g. 12" />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={deleteWorkPackage} disabled={!workPackageId || deletingWorkPackage}>
              {deletingWorkPackage ? "Deleting…" : "Delete Work Package"}
            </BtnDanger>
            <BtnGeneral onClick={() => setWorkPackageId("")} disabled={!workPackageId}>Clear</BtnGeneral>
          </div>
          <div className="md:col-span-3 text-xs text-gray-600">
            Deletes only an unstarted Work Package. Recorded work blocks deletion (409).
            The parent Job, shared Locations, and workflow templates are preserved.
          </div>
        </div>
      </Section>

      <Section title="Delete Runtime Task (deep)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Task ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteTask)}
              placeholder="e.g. 123"
            />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={deleteTask} disabled={!taskId || deletingTask}>
              {deletingTask ? "Deleting…" : "Delete Runtime Task (deep)"}
            </BtnDanger>
            <BtnGeneral onClick={() => setTaskId("")} disabled={!taskId}>
              Clear
            </BtnGeneral>
          </div>
          <div className="md:col-span-3 text-xs text-gray-600">
            Data Integrity cleanup only. Deletes the runtime task and its task-owned runtime records; the workflow template and template step remain unchanged.
          </div>
        </div>
      </Section>

      <Section title="Delete Expense">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Expense ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={expenseId}
              onChange={(e) => setExpenseId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteExpense)}
              placeholder="e.g. 42"
              inputMode="numeric"
            />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={deleteExpense} disabled={!Number.isSafeInteger(Number(expenseId)) || Number(expenseId) < 1 || deletingExpense}>
              {deletingExpense ? "Deleting…" : "Delete Expense"}
            </BtnDanger>
            <BtnGeneral onClick={() => setExpenseId("")} disabled={!expenseId || deletingExpense}>
              Clear
            </BtnGeneral>
          </div>
          <div className="md:col-span-3 text-xs text-gray-600">
            Permanently deletes the specified expense. Super Admin and System Admin only; the backend enforces tenant isolation.
          </div>
        </div>
      </Section>

      <Section title="Delete Contact">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Contact ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteContact)}
              placeholder="e.g. 7"
            />
          </div>
          <label className="min-h-[44px] md:min-h-0 py-2 md:py-0 flex items-center gap-3 md:gap-2 text-sm">
            <input className="h-4 w-4 shrink-0" type="checkbox" checked={detachJobs} onChange={(e) => setDetachJobs(e.target.checked)} />
            Detach jobs (set contact_id = NULL) instead of deleting jobs
          </label>
          <div>
            <BtnDanger onClick={deleteContact} disabled={!contactId || deletingContact}>
              {deletingContact ? "Deleting…" : "Delete Contact"}
            </BtnDanger>
          </div>
        </div>
      </Section>

      <Section title="Delete User">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">User ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteUser)}
              placeholder="e.g. 5"
            />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={deleteUser} disabled={!userId || deletingUser}>
              {deletingUser ? "Deleting…" : "Delete User"}
            </BtnDanger>
            <BtnGeneral onClick={() => setUserId("")} disabled={!userId}>
              Clear
            </BtnGeneral>
          </div>
        </div>
      </Section>

      <Section title="Redact Resource (GDPR-safe)">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Resource ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={resourceId}
              onChange={(e) => setResourceId(e.target.value)}
              onKeyDown={(e) => onEnter(e, redactResource)}
              placeholder="e.g. 5"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Replacement Resource ID (optional)</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={replacementResourceId}
              onChange={(e) => setReplacementResourceId(e.target.value)}
              onKeyDown={(e) => onEnter(e, redactResource)}
              placeholder="e.g. 12 (reassign refs first)"
            />
          </div>

          <div className="md:col-span-2 flex flex-col md:flex-row gap-2">
            <BtnDanger onClick={redactResource} disabled={!resourceId || redactingResource}>
              {redactingResource ? "Redacting…" : "Redact Resource"}
            </BtnDanger>
            <BtnGeneral
              onClick={() => {
                setResourceId("");
                setReplacementResourceId("");
              }}
              disabled={!resourceId && !replacementResourceId}
            >
              Clear
            </BtnGeneral>
          </div>

          <div className="md:col-span-4 text-xs text-gray-600">
            Success shows an INFO toast; failures show CRITICAL. Use replacement resource ID to migrate ownership first.
          </div>
        </div>
      </Section>

      <Section title="Delete Customer">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Customer ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteCustomer)}
              placeholder="e.g. 3"
            />
          </div>
          <label className="min-h-[44px] md:min-h-0 py-2 md:py-0 flex items-center gap-3 md:gap-2 text-sm">
            <input className="h-4 w-4 shrink-0" type="checkbox" checked={cascadeJobs} onChange={(e) => setCascadeJobs(e.target.checked)} />
            Cascade jobs (deep-delete all customer jobs)
          </label>
          <label className="min-h-[44px] md:min-h-0 py-2 md:py-0 flex items-center gap-3 md:gap-2 text-sm">
            <input className="h-4 w-4 shrink-0" type="checkbox" checked={cascadeContacts} onChange={(e) => setCascadeContacts(e.target.checked)} />
            Cascade contacts
          </label>
          <div className="md:col-span-3">
            <BtnDanger onClick={deleteCustomer} disabled={!customerId || deletingCustomer}>
              {deletingCustomer ? "Deleting…" : "Delete Customer"}
            </BtnDanger>
          </div>
        </div>
      </Section>

      <Section title="Delete Location">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Location ID</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              onKeyDown={(e) => onEnter(e, deleteLocation)}
              placeholder="e.g. 6"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Reassign jobs to (optional)</label>
            <input
              className="w-full min-h-[44px] md:min-h-0 border rounded px-3 py-2 text-sm bg-white"
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              placeholder="e.g. 4 (leave empty to clear)"
            />
          </div>
          <div className="md:col-span-2">
            <BtnDanger onClick={deleteLocation} disabled={!locationId || deletingLocation}>
              {deletingLocation ? "Deleting…" : "Delete Location"}
            </BtnDanger>
          </div>
        </div>
      </Section>

      <Section title="Database Backup & Restore">
        <div className="flex flex-col md:flex-row md:flex-wrap gap-3 md:items-end">
          <BtnAction onClick={doBackup} disabled={backingUp}>
            {backingUp ? "Backing up…" : "Create Backup"}
          </BtnAction>

          <div className="text-sm text-gray-700">Existing backups:</div>
          <div className="min-w-0 w-full md:w-auto flex flex-col md:flex-row md:flex-wrap gap-2">
            {backups.length === 0 ? (
              <span className="text-sm text-gray-500 italic">none</span>
            ) : (
              backups.map((b) => (
                <a
                  key={b.name}
                  href={b.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full md:w-auto max-w-full break-all text-xs bg-blue-50 border border-blue-200 rounded px-3 py-2 md:px-2 md:py-1 text-blue-700 hover:bg-blue-100"
                  title={`${b.name} (${b.size ?? "?"} bytes)`}
                >
                  {b.name}
                </a>
              ))
            )}
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Restore from SQL dump</label>
              <input
                id="restore-file-input"
                type="file"
                accept=".sql"
                className="block w-full max-w-full overflow-hidden text-sm"
                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              />
              <label className="mt-2 min-h-[44px] md:min-h-0 py-2 md:py-0 flex items-center gap-3 md:gap-2 text-xs text-red-700">
                <input className="h-4 w-4 shrink-0" type="checkbox" checked={truncateBefore} onChange={(e) => setTruncateBefore(e.target.checked)} />
                DROP & recreate schema before restore (DANGEROUS!)
              </label>
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <BtnAction onClick={doRestore} disabled={!restoreFile || restoring}>
                {restoring ? "Restoring…" : "Restore"}
              </BtnAction>
              <BtnGeneral
                onClick={() => {
                  setRestoreFile(null);
                  const el = document.getElementById("restore-file-input");
                  if (el) el.value = "";
                }}
                disabled={!restoreFile || restoring}
              >
                Clear
              </BtnGeneral>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default AdminMaintenanceWidget;
